import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { accessorialSchema } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import { parseAccessorialsFromDetail } from "~/shared/utils/address-book-accessorials.ts";

export const useAddressBookAccessorials = () => {
	const { onChange } = useContext(QuoteDetailsContext);

	const { data: allAccessorials } = useQuery({
		queryKey: ["accessorials"],
		queryFn: async () => {
			const result = await getAndValidate(
				apiRoutes.ACCESSORIALS,
				accessorialSchema.array(),
			);
			return result.success ? result.data : [];
		},
		staleTime: 1000 * 60 * 60, // 1 hour
	});

	const applyAddressBookAccessorials = useCallback(
		(name: "pickup" | "delivery", address: AddressBookItemSummary) => {
			const targetStopType = name === "pickup" ? 1 : 2;
			const detail = address.details.find((d) => d.stopType === targetStopType);

			if (!detail || !allAccessorials) return;

			const addressAccessorials = parseAccessorialsFromDetail(
				detail,
				allAccessorials,
			);

			if (addressAccessorials.length > 0) {
				onChange(
					`${name}Services` as keyof QuoteStateType,
					addressAccessorials,
				);
			}
		},
		[allAccessorials, onChange],
	);

	return { applyAddressBookAccessorials, allAccessorials };
};
